import { useState } from 'react'
import { PaintBucket, Type } from 'lucide-react'
import { Button, ColorSwatchPicker } from '@/components/ds2'

interface QuickColorControlProps {
    label: string
    colors: string[]
    fallback: string
    value?: string
    onChange: (color: string | null) => void
}

interface TemplateSimpleAppearanceControlsProps {
    emailBackground: string
    onTextColorChange: (color: string | null) => void
    onTextBackgroundChange: (color: string | null) => void
    onEmailBackgroundChange: (color: string | null) => void
}

const TEXT_COLORS = ['#18181b', '#52525b', '#b91c1c', '#c2410c', '#15803d', '#0369a1', '#1d4ed8', '#6d28d9']
const BACKGROUND_COLORS = ['#ffffff', '#f8fafc', '#f1f5f9', '#eff6ff', '#f0fdf4', '#fff7ed', '#fef2f2', '#2c3e50']
const HIGHLIGHT_COLORS = ['#ffffff', '#fef9c3', '#ffedd5', '#dcfce7', '#dbeafe', '#ede9fe', '#fce7f3']

const colorInputValue = (color: string, fallback: string): string => {
    const normalized = color.trim().toLowerCase()
    if (/^#[0-9a-f]{6}$/.test(normalized)) return normalized
    const rgb = normalized.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/)
    if (!rgb) return fallback
    return `#${rgb
        .slice(1, 4)
        .map((channel) => Math.min(255, Number(channel)).toString(16).padStart(2, '0'))
        .join('')}`
}

const QuickColorControl = ({ label, colors, fallback, value = '', onChange }: QuickColorControlProps) => {
    const [selected, setSelected] = useState(() => colorInputValue(value, fallback))
    const displayedColor = value ? colorInputValue(value, fallback) : selected

    const selectColor = (color: string) => {
        setSelected(color)
        onChange(color)
    }

    return (
        <div className="flex min-w-0 flex-col gap-2 rounded-xl bg-surface p-3 shadow-field">
            <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-foreground">{label}</span>
                <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Clear ${label.toLowerCase()}`}
                    onClick={() => {
                        setSelected(fallback)
                        onChange(null)
                    }}
                >
                    Clear
                </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <ColorSwatchPicker colors={colors} value={displayedColor} onChange={selectColor} size="sm" />
                <label className="flex items-center gap-1 text-xs text-foreground-muted">
                    Custom
                    <input
                        type="color"
                        aria-label={`Custom ${label.toLowerCase()}`}
                        value={displayedColor}
                        onChange={(event) => selectColor(event.target.value)}
                        className="size-7 cursor-pointer rounded-lg bg-transparent p-0"
                    />
                </label>
            </div>
        </div>
    )
}

export const TemplateSimpleAppearanceControls = ({
    emailBackground,
    onTextColorChange,
    onTextBackgroundChange,
    onEmailBackgroundChange,
}: TemplateSimpleAppearanceControlsProps) => (
    <div className="flex flex-col gap-3 rounded-2xl bg-surface-secondary p-3">
        <div className="flex items-start gap-2">
            <PaintBucket className="mt-0.5 size-4 shrink-0 text-foreground-muted" />
            <div>
                <p className="text-sm font-medium text-foreground">Quick colors</p>
                <p className="text-xs text-foreground-muted">
                    Select text in the editor before changing text colors. Email background changes the whole template.
                </p>
            </div>
        </div>
        <div className="grid gap-2 md:grid-cols-3">
            <QuickColorControl label="Text color" colors={TEXT_COLORS} fallback="#18181b" onChange={onTextColorChange} />
            <QuickColorControl label="Text background" colors={HIGHLIGHT_COLORS} fallback="#ffffff" onChange={onTextBackgroundChange} />
            <QuickColorControl
                label="Email background"
                colors={BACKGROUND_COLORS}
                fallback="#ffffff"
                value={emailBackground}
                onChange={onEmailBackgroundChange}
            />
        </div>
        <div className="flex items-center gap-2 text-xs text-foreground-muted">
            <Type className="size-4 shrink-0" />
            <span>You can also use the editor toolbar for bold, underline, font size, lists, and links.</span>
        </div>
    </div>
)
